import { useEffect, useState } from 'react';
import { supabase } from '../supabase';

export interface MemberOption {
  user_id: string;
  display_name: string;
  username?: string | null;
}

export interface ProjectSheetData {
  members: ReadonlyArray<MemberOption>;
  stages: ReadonlyArray<string>;
  loading: boolean;
}

/** Org members (lead picker) + the org's project status stages (status pills). */
export function useProjectSheetData(activeOrgId: string | null, enabled: boolean): ProjectSheetData {
  const [members, setMembers] = useState<ReadonlyArray<MemberOption>>([]);
  const [stages, setStages] = useState<ReadonlyArray<string>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activeOrgId || !enabled) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const [membersRes, pipeRes] = await Promise.all([
        supabase.rpc('list_org_members', { p_org_id: activeOrgId }),
        supabase
          .from('project_status_pipelines')
          .select('stages, read_only, name')
          .eq('org_id', activeOrgId)
          .order('read_only', { ascending: false })
          .limit(1)
          .maybeSingle()
      ]);
      if (cancelled) return;

      const opts: MemberOption[] = [];
      for (const m of (membersRes.data ?? []) as ReadonlyArray<Record<string, unknown>>) {
        const id = m.user_id as string | undefined;
        const name = ((m.display_name ?? m.username ?? '') as string).trim();
        if (id && name) opts.push({ user_id: id, display_name: name, username: (m.username as string) ?? null });
      }
      opts.sort((a, b) => a.display_name.localeCompare(b.display_name));
      setMembers(opts);

      const raw = (pipeRes.data?.stages ?? []) as unknown;
      setStages(Array.isArray(raw) ? raw.filter((s): s is string => typeof s === 'string') : []);
      setLoading(false);
    })().catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [activeOrgId, enabled]);

  return { members, stages, loading };
}
